/**
 * Time units, and the organisation's working-time model that gives them length.
 *
 * ── NOTHING HERE IS A CONSTANT ──────────────────────────────────────────────
 *
 * "A day" and "a week" have no fixed length until an organisation states its
 * own working day. A module that assumed 24 h/day or 52 weeks/year would
 * inflate every percentage it touched, invisibly and by a lot: 46 working weeks
 * versus 52 moves a coverage figure by 13%. So this file defines the RULE for
 * converting units and defines no defaults at all. The defaults live in
 * `OrganizationAutomationSettings`, they are per-organisation, and they reach
 * the evaluator through its context — never through an import.
 *
 * ── ONE CONVERSION RULE, DERIVED FROM THE DIMENSION ─────────────────────────
 *
 * Every quantity with a TIME exponent is canonicalised to HOURS before it
 * enters the evaluator. The magnitude is scaled by `factor ^ timeExponent`,
 * where `factor` is the unit's length in hours. One rule covers every case,
 * including the ones people get wrong:
 *
 *     35, TIME¹,           unit "hour"     → 35 × 1        = 35 h
 *     12, TIME¹·EVENT⁻¹,   unit "minute"   → 12 × (1/60)   = 0.2 h/event
 *     40, EVENT¹·TIME⁻¹,   unit "week"     → 40 ÷ weekHours = tickets per hour
 *
 * The third line is the one that fails under a naive implementation: a "per
 * week" frequency must be DIVIDED by the week length, and it is, because its
 * TIME exponent is -1. No special-casing, no separate table of inverse units.
 *
 * ── DETERMINISM ─────────────────────────────────────────────────────────────
 *
 * `Math.pow` is explicitly implementation-approximated by ECMA-262, so it is
 * never used. Scaling runs a fixed loop of IEEE-754 `*` and `/`, both of which
 * are exactly specified, so the same input yields the same bits on every
 * conformant engine.
 */
import { z } from "zod";

/** The units an authored input may be stated in. */
export const COVERAGE_TIME_UNITS = ["minute", "hour", "day", "week", "month", "year"] as const;

/** A unit an authored input may be stated in. */
export type CoverageTimeUnit = (typeof COVERAGE_TIME_UNITS)[number];

/** The unit assumed when an input declares none. The identity of the scaling rule. */
export const COVERAGE_CANONICAL_TIME_UNIT: CoverageTimeUnit = "hour";

/**
 * The organisation's working-time model — the shape the evaluator needs out of
 * `OrganizationAutomationSettings`.
 *
 * A structural subset on purpose: this module must not depend on the Prisma
 * row, and a `RoleWorkload` may override any of these per role.
 */
export interface CoverageWorkingTimeSettings {
  /** Hours in one working day. */
  readonly hoursPerDay: number;
  /** Working days in one week. */
  readonly daysPerWeek: number;
  /** Weeks actually worked in one year — holidays and leave already removed. */
  readonly workingWeeksPerYear: number;
}

/**
 * Validation for the working-time model.
 *
 * Every field must be finite and strictly positive: a zero working day makes
 * every "per day" conversion a division by zero, and a negative one makes
 * percentages negative. Rejecting here means the evaluator never has to.
 */
export const CoverageWorkingTimeSettingsSchema = z.strictObject({
  hoursPerDay: z
    .number()
    .refine((n) => Number.isFinite(n) && n > 0, { message: "hoursPerDay must be finite and > 0" }),
  daysPerWeek: z
    .number()
    .refine((n) => Number.isFinite(n) && n > 0, { message: "daysPerWeek must be finite and > 0" }),
  workingWeeksPerYear: z.number().refine((n) => Number.isFinite(n) && n > 0, {
    message: "workingWeeksPerYear must be finite and > 0"
  })
});

/**
 * The length of one unit, in hours, under this organisation's working model.
 *
 * A `month` is one twelfth of the organisation's WORKING year, not a calendar
 * month. Stating it that way keeps `12 × month === year` exactly true, which is
 * the property an author relies on; a calendar month would not hold it.
 */
export function hoursInCoverageTimeUnit(
  unit: CoverageTimeUnit,
  settings: CoverageWorkingTimeSettings
): number {
  const day = settings.hoursPerDay;
  const week = day * settings.daysPerWeek;
  const year = week * settings.workingWeeksPerYear;

  switch (unit) {
    case "minute":
      return 1 / 60;
    case "hour":
      return 1;
    case "day":
      return day;
    case "week":
      return week;
    case "month":
      return year / 12;
    case "year":
      return year;
  }
}

/**
 * Scale a magnitude by `factor ^ exponent`, using only exactly-specified IEEE
 * operations in a fixed order.
 *
 * `Math.pow` is deliberately avoided: ECMA-262 permits an implementation-defined
 * approximation, which would let the browser and the server disagree on the
 * fourth decimal of a customer-facing number.
 */
export function scaleByCoverageTimeExponent(
  value: number,
  factor: number,
  exponent: number
): number {
  const repetitions = exponent < 0 ? -exponent : exponent;
  let scaled = value;
  for (let i = 0; i < repetitions; i += 1) {
    scaled = exponent > 0 ? scaled * factor : scaled / factor;
  }
  return scaled;
}
